import React from 'react';
import { Plus, X } from 'lucide-react';
import TimeInput from '@/components/ui/time-input';
import { WEEKDAYS, formatAvailability } from '@/lib/availability';

const EMPTY_WINDOW = { weekday: '', from: '', to: '' };

export default function StepSlot({ form, setRoot }) {
  const rows = form.availability?.length ? form.availability : [EMPTY_WINDOW];

  const write = (next) => setRoot('availability', next);

  const update = (index, patch) => {
    write(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const add = () => write([...rows, { ...EMPTY_WINDOW }]);

  const remove = (index) => {
    const next = rows.filter((_, i) => i !== index);
    write(next.length ? next : [{ ...EMPTY_WINDOW }]);
  };

  const preview = formatAvailability(rows);

  return (
    <div className="space-y-5" data-testid="public-registration-step-slot">
      <h2 className="text-lg font-semibold text-slate-900">
        An welchen Tagen und zu welchen Uhrzeiten ist Unterricht möglich?
      </h2>
      <p className="text-sm text-slate-500">
        Mehrere Tage sind möglich. Ohne Endzeit gilt der Tag ab der genannten Uhrzeit,
        zum Beispiel „Montag ab 15:00 Uhr“ oder „Mittwoch 14:00–17:00 Uhr“.
      </p>
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-end"
            data-testid={`public-registration-availability-row-${i}`}
          >
            <label className="block text-xs text-slate-600">
              Tag
              <select
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={row.weekday}
                onChange={(e) => update(i, { weekday: e.target.value })}
                data-testid={`public-registration-availability-weekday-${i}`}
              >
                <option value="">Bitte wählen</option>
                {WEEKDAYS.map((day) => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-600">
              Von
              <TimeInput
                className="mt-1"
                value={row.from}
                onChange={(e) => update(i, { from: e.target.value })}
                data-testid={`public-registration-availability-from-${i}`}
              />
            </label>
            <label className="block text-xs text-slate-600">
              Bis (optional)
              <TimeInput
                className="mt-1"
                value={row.to}
                onChange={(e) => update(i, { to: e.target.value })}
                data-testid={`public-registration-availability-to-${i}`}
              />
            </label>
            <button
              type="button"
              className="h-10 w-10 rounded-md border border-slate-200 text-slate-500 hover:text-slate-800"
              onClick={() => remove(i)}
              aria-label="Zeitfenster entfernen"
              data-testid={`public-registration-availability-remove-${i}`}
            >
              <X className="h-4 w-4 mx-auto" />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="inline-flex items-center gap-2 text-sm font-medium text-brand-red"
        onClick={add}
        data-testid="public-registration-availability-add"
      >
        <Plus className="h-4 w-4" /> Weiteres Zeitfenster
      </button>

      <div className="text-sm text-slate-700" data-testid="public-registration-availability-preview">
        {preview}
      </div>
    </div>
  );
}
